import { Business, Payment, Person, Reservation, Service, ServiceBooking } from "@turizi/shared";
import { IconId, IconMail, IconPhone, IconArrowRight, IconUser } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { MenuItem } from "./left-menu";
import { TagIcon } from "@/lib/tag-utils";
import { formatDayShort, formatDay, formatPrice } from "@/lib/format-utils";
import { forwardRef } from "react";
import { ServiceTitle, ServiceTitleStatic } from "./services/service-title";
import { ReservationStatusBadge, ReservationClient } from './reservation-ui';
import { BookingGroupSize, ShowPerson } from "./snippets";

interface RowProps {
    onClick: () => void;
    highlighted: boolean;
}

export const SearchReservationRow = forwardRef<HTMLDivElement, RowProps & { reservation: Reservation }>(
    ({ reservation, onClick, highlighted }, ref) => {
        const resGroupNames = (reservation.pax_groups || []).flatMap(g => [
            g.name,
            ...(g.persons || []).map(p => p.name)
        ]);
        const bookingGroupNames = (reservation.service_bookings || []).flatMap(b =>
            (b.pax_groups || []).flatMap(pg => [
                pg.pax_group?.name,
                ...(pg.pax_group?.persons || []).map(p => p.name)
            ])
        );
        const uniquePaxNames = Array.from(new Set([...resGroupNames, ...bookingGroupNames].filter(Boolean)));

        const categoryCounts = (reservation.service_bookings || []).reduce<Record<string, number>>((acc, b) => {
            const cat = b.category || b.service_data?.category || 'service';
            acc[cat] = (acc[cat] || 0) + 1;
            return acc;
        }, {});

        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`p-2 rounded-lg cursor-pointer transition-all flex flex-col sm:flex-row items-start gap-2 sm:gap-3 w-full
                    ${highlighted
                        ? 'bg-gray-100 text-black'
                        : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-start gap-3 w-full min-w-0">
                    <div className="flex items-center gap-1.5 shrink-0 w-[100px]">
                        <TagIcon name="reservations" size="xs" hideText className="shrink-0" />
                        <span className="font-bold text-xs sm:text-sm text-gray-900">#{reservation.id}</span>
                    </div>
                    <div className="flex flex-col shrink-0 w-[130px] min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <ReservationStatusBadge reservation={reservation} showText={false} />
                            <span className="font-medium text-xs sm:text-sm text-gray-900 truncate">
                                {formatDayShort(reservation.start_day)} <span className="text-gray-300">→</span> {formatDayShort(reservation.end_day)}
                            </span>
                        </div>
                        {reservation.client_business && (
                            <div className="text-xs font-medium text-gray-500 truncate mt-0.5">
                                <ReservationClient reservation={reservation} />
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-1 min-w-0">
                        <div className="flex flex-wrap gap-0.5 min-w-0">
                            {uniquePaxNames.map(name => (
                                <div
                                    key={name}
                                    className="text-xs flex gap-0.5 items-center solid-box-border text-gray-900"
                                >
                                    <IconUser color="gray" size={8} />{name}
                                </div>
                            ))}
                        </div>
                        {Object.keys(categoryCounts).length > 0 && (
                            <div className="flex flex-col items-center gap-0.5 items-end shrink-0">
                                {Object.entries(categoryCounts).map(([cat, count]) => (
                                    <div key={cat} className="p-0.5 flex items-center gap-0.5 text-xs text-gray-400 font-medium">
                                        <span>{count}</span>
                                        <TagIcon name={cat} size="xs" hideText noBackground />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

export const SearchPersonRow = forwardRef<HTMLDivElement, RowProps & { person: Person }>(
    ({ person, onClick, highlighted }, ref) => {
        const { t } = useTranslation();
        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`p-2 rounded-lg cursor-pointer transition-all flex items-start justify-between gap-3 w-full ${highlighted
                    ? 'bg-gray-100 text-black'
                    : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <ShowPerson person={person} />
                </div>
                <div className="text-xs text-gray-800 font-light shrink-0 mt-0.5">
                    #{person.id}
                </div>
            </div>
        );
    }
);

export const SearchBusinessRow = forwardRef<HTMLDivElement, RowProps & { business: Business }>(
    ({ business, onClick, highlighted }, ref) => {
        const { t } = useTranslation();
        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`p-2 rounded-lg cursor-pointer transition-all flex items-start justify-between gap-3 w-full ${highlighted
                    ? 'bg-gray-100 text-black'
                    : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <TagIcon name={business.category} size="xs" hideText className="shrink-0 mt-2" />
                    <div className="flex flex-col min-w-0">
                        <div className="font-medium text-sm text-gray-900 leading-snug truncate">
                            {business.name}
                        </div>
                        <div className="text-xs text-gray-500 font-medium mt-0.5 truncate">
                            {[t(business.category || ''), business.city, business.country_code].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                </div>
                <div className="text-xs text-gray-800 font-light shrink-0 mt-0.5">
                    #{business.id}
                </div>
            </div>
        );
    }
);

export const SearchServiceRow = forwardRef<HTMLDivElement, RowProps & { service: Service }>(
    ({ service, onClick, highlighted }, ref) => {
        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`p-2 rounded-lg cursor-pointer transition-all flex items-start justify-between gap-3 w-full ${highlighted
                    ? 'bg-gray-100 text-black'
                    : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <TagIcon name={service.category || 'services'} size="xs" hideText noBackground className="shrink-0 mt-2" />
                    <div className="text-sm font-medium text-gray-900 min-w-0">
                        <ServiceTitle service={service} hideProviders />
                    </div>
                </div>
                <div className="text-xs text-gray-800 font-light shrink-0 mt-0.5">
                    #{service.id}
                </div>
            </div>
        );
    }
);

export const SearchPaymentRow = forwardRef<HTMLDivElement, RowProps & { payment: Payment }>(
    ({ payment, onClick, highlighted }, ref) => {
        const { t } = useTranslation();
        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`p-2 rounded-lg cursor-pointer transition-all flex flex-col sm:flex-row items-start justify-between gap-2 sm:gap-3 w-full ${highlighted
                    ? 'bg-gray-100 text-black'
                    : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <TagIcon name="money_transactions" size="xs" hideText className="shrink-0 mt-2" />
                    <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-extrabold text-gray-900">
                                {formatPrice(payment.amount, { currency: payment.currency })}
                            </span>
                        </div>
                        <div className="text-xs text-gray-500 font-medium truncate mt-0.5">
                            {payment.from_name} → {payment.to_name} <span className="text-xs text-gray-800 font-light mt-0.5">#{payment.id}</span>
                        </div>
                    </div>
                </div>

                {payment.notes && (
                    <div className="text-xs text-gray-600 italic truncate max-w-[320px] mt-0.5" title={payment.notes}>
                        "{payment.notes}"
                    </div>
                )}
                <div className="flex flex-col items-end gap-0.5 text-xs text-gray-400 shrink-0">
                    {payment.payment_date && (
                        <span className="font-medium text-gray-900">{formatDay(payment.payment_date)}</span>
                    )}
                    {payment.payment_method && (
                        <div className="text-xxs solid-box-border">
                            {t(`payment_methods.${payment.payment_method}`) || t(payment.payment_method)}
                        </div>
                    )}
                    {payment.external_reference && (
                        <span className="text-gray-400 text-[11px] ">
                            Ref: {payment.external_reference}
                        </span>
                    )}
                </div>
            </div>
        );
    }
);

export const SearchMenuLinkRow = forwardRef<HTMLDivElement, RowProps & { menuLink: MenuItem }>(
    ({ menuLink, onClick, highlighted }, ref) => {
        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`px-3 py-2 rounded-lg cursor-pointer transition-all flex items-center justify-between gap-3 w-full ${highlighted
                    ? 'bg-black text-white font-medium'
                    : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`shrink-0 ${highlighted ? 'text-white' : 'text-gray-600'}`}>
                        {menuLink.icon}
                    </div>
                    <span className="font-medium text-sm truncate">
                        {menuLink.label}
                    </span>
                </div>
                <IconArrowRight size={16} className={`shrink-0 ${highlighted ? 'text-white' : 'text-gray-400'}`} />
            </div>
        );
    }
);

export const SearchServiceBookingRow = forwardRef<HTMLDivElement, RowProps & { booking: ServiceBooking }>(
    ({ booking, onClick, highlighted }, ref) => {
        const { t } = useTranslation();
        const serviceTitleText = booking.service_data
            ? ServiceTitleStatic(booking.service_data)
            : (t(booking.category || '') || booking.category || 'Service Booking');

        const serviceOrderId = booking.service_order_id
            || booking.service_order_bookings?.[0]?.service_order_id
            || booking.service_order_bookings?.[0]?.service_order?.id;

        return (
            <div
                ref={ref}
                onClick={onClick}
                className={`p-2 rounded-lg cursor-pointer transition-all flex items-center justify-between gap-3 w-full ${highlighted
                    ? 'bg-gray-100 text-black'
                    : 'hover:bg-gray-100 text-gray-900'
                    }`}
            >
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <TagIcon name={booking.category || 'services'} size="xs" hideText className="shrink-0 mt-2" />

                    <div className="space-y-1">
                        {booking.reservation_id ? (
                            <span className="text-gray-900 text-xs font-medium">
                                {t('reservation')} #{booking.reservation_id}
                            </span>
                        ) : serviceOrderId ? (
                            <span className="text-gray-900 text-xs font-medium">
                                {t('service_order')} #{serviceOrderId}
                            </span>
                        ) : null}
                        <div className="font-medium text-sm text-gray-900 line-clamp-2" title={serviceTitleText}>
                            {serviceTitleText}
                        </div>
                        <div className="flex items-center gap-2 text-xs mt-0.5 text-gray-500 flex-wrap">
                            {booking.start_day && (
                                <span className="font-medium text-gray-700">
                                    {formatDayShort(booking.start_day)}
                                    {booking.end_day && booking.end_day !== booking.start_day && ` → ${formatDayShort(booking.end_day)}`}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <BookingGroupSize booking={booking as any} variant="fit" />
                        </div>
                    </div>
                </div>
                <span className="text-xs text-gray-800 font-light">#{booking.id}</span>
            </div>
        );
    }
);
